import {
    getAllResultStateInfos, getAllSearchResultParams, getDataModelState, getItemId, getLoadedModels, getParentId,
    getSubviews
} from "~/stateGetter";
import modelTree from "~/selectors/modelTree";
import concludeAbout from "~/lib/conclude";
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import min from 'lodash/min';
import max from 'lodash/max';
import first from 'lodash/first';
import values from 'lodash/values';
import pick from 'lodash/pick';
import {presentAllResultParams} from "~/statePresenter";
import identify from "~/identify";

//TODO:
/*
This is inperformant, while not tested always a performance overhead on every redux action, ~ 3-4 ms
We need to build in a shouldComponentUpdate function to not always check everything
Also I feel that in the mocked value functions we do not handle error/cancel correctly, need to revisit that
 */

const loadingSchema = concludeAbout([
    { name: 'authResult' },
    { name: 'logoutResult' },
    { 
        name: 'models',
        ignore: true,
        emit: (state, {value}) => {
            return map(value, (model, modelName) => `models.${modelName}`);
        }
        
    },
    {
        name: 'modelCacheStatus',
    },
    {
        name: 'ui.searchModels',
        emit: (state, {value}) => {
            return map(value, (model, modelName) => `ui.searchModels.${modelName}.resultsSearching`);
        }
    },
    {
        name: 'ui.searchModels.{modelName}.results',
        value: (state, {parameters, objectPath}) => {
            let value = get(state, objectPath);

            try {
                let resultParams = getAllSearchResultParams(state, parameters.modelName);

                if(!isEqual(resultParams, value.loadedParams)) {
                    return {
                        ...value,
                        status: identify.STATUS_INVALIDATED
                    };
                }
            } catch(error) { return value; }

            return value;
        }
    },

    {
        name: 'ui.searchModels.{modelName}.resultsSearching',
        value: (state, {parameters}) => {
            let value = get(state, `ui.searchModels.${parameters.modelName}.results`);

            if(value.status === identify.STATUS_ERRORED) {
                return value;
            }

            let resultParams = getAllSearchResultParams(state, parameters.modelName);

            if(!resultParams.filters || !value.loadedParams || ! value.loadedParams.filters) {
                return { ...value, status: identify.STATUS_STARTED };
            }

            if(isEqual(resultParams.filters, value.loadedParams.filters)) {
                return {
                    ...value,
                    status: identify.STATUS_SUCCESS
                };
            } else {
                return { ...value, status: identify.STATUS_STARTED };
            }
        }
    },

    { name: 'models.{modelName}' },
    {
        name: 'loadedModels.{modelId}',
        value: (state, {parameters}) => {
            try {
                return getDataModelState(state, parameters.modelId)
            } catch(e) {
                return null;
            }
        }
    },
    {
        name: 'loadedModels.{modelId}.subviews',
        ignore: true,
        emit: (state, {parameters}) => {
           let subviews = getSubviews(state, parameters.modelId);
           return map(subviews, (subview) => `models.${subview.model}`);
        }
    },
    {
        name: 'ui.loadedModels.{modelId}.results',
        value: (state, {objectPath, parameters}) => {
            let value = get(state, objectPath);

            try {
                let { loadedResultParams, ...resultProps } = getAllResultStateInfos(state, parameters.modelId),
                    resultParams = presentAllResultParams(resultProps);

                if(!isEqual(resultParams, loadedResultParams)) {
                    return {
                        ...value,
                        status: identify.STATUS_INVALIDATED
                    };
                }
            } catch(error) { return value; }

            return value;
        }
    },

    {
        name: 'ui.loadedModels.{modelId}.resultsInvalid',
        path: false,
        value: (state, {parameters}) => {
            let value = get(state, `ui.loadedModels.${parameters.modelId}.results`);

            if(!value) {
                return value;
            }

            let { loadedResultParams, ...resultProps } = getAllResultStateInfos(state, parameters.modelId),
                resultParams = presentAllResultParams(resultProps);

            loadedResultParams = pick(loadedResultParams, ['filters']);
            resultParams = pick(resultParams, ['filters'])

            let isInvalid = !isEqual(resultParams, loadedResultParams);

            if(isInvalid) {
                return {
                    ...value,
                    status: identify.STATUS_INVALIDATED
                };
            }
            return {
                ...value,
                status: value.status === identify.STATUS_INVALIDATED ? identify.STATUS_STARTED : value.status
            };
        }
    },

    {
        name: 'ui.loadedModels.{modelId}.itemData',
        value: (state, {objectPath, parameters}) => {
            let value = get(state, objectPath),
                itemId = getItemId(state, parameters.modelId);

            if(itemId !== identify.NEW_ITEM && itemId !== get(value, 'value.itemId')) {
                return {
                    ...value,
                    status: 'started_at',
                    finished_at: null
                }
            }

            return value;
        }
    },
    { name: 'ui.loadedModels.{modelId}.saveResult' },
    { name: 'ui.loadedModels.{modelId}.deleteResult' },
    { name: 'ui.loadedModels.{modelId}.itemCustomActionResult' },
    { name: 'ui.loadedModels.{modelId}.customActionResult' },
    {
        name: 'ui.loadedModels.{modelId}.subviews',
        ignore: true,
        emit: (state, {parameters}) => {
            let itemId = getItemId(state, parameters.modelId);

            if(!itemId || itemId === identify.NEW_ITEM) {
                return [];
            }

            let subviewPaths = modelTree(getLoadedModels(state))
                .getChildren(parameters.modelId)
                .map((model) => {
                    return `ui.loadedModels.${model.modelId}`;
                });

            return subviewPaths
        }
    },
    {
        name: 'ui.loadedModels.{modelId}.parent',
        ignore: true,
        emit: (state, {parameters}) => {
            let parentModelId = getParentId(state, parameters.modelId);
            if(!parentModelId) { return []; }
            return [`ui.loadedModels.${parentModelId}`];
        }
    },
    // toDO
    {
        name: 'models.{modelId}.options',
        ignore: true,
        emit: (state, {modelId}) => {

        }
    },
    // toDo
    {
        name: 'models.{modelId}.filterOptions',
        ignore: true,
    },
]);

const isLoading = (value) => {
    let status = get(value, 'status');
    return status === identify.STATUS_STARTED || status === identify.STATUS_INVALIDATED;
};

const isInvalidated = (value) => {
    let status = get(value, 'status');


    if(status === identify.STATUS_INVALIDATED) {
        // console.log('CONCLUDING XX', value);
    }
    return status === identify.STATUS_INVALIDATED;
};

const isPresent = (value) => {
    return !!get(value, 'finished_at');
};

const isNotPresent = (value) => {
    return !isPresent(value);
};

const reduceStatus = ({isWrongWhen, isTrueWhen}) => {
    return (prev, props) => {
        let result = ((prev, {value}) => {
            if(!value) {
                return { _break: !isWrongWhen };
            }

            if(value && 'finished_at' in value) {
                if(isTrueWhen && isTrueWhen(value)) {
                    return { _break: true };
                }

                if(isWrongWhen && isWrongWhen(value)) {
                    return { _break: false };
                }
            }

            return prev;
        })(prev, props);

        return result;
    }
};

const reduceFalsify = (prev, {value}) => {
    if(!value) { return {_break: false}; }
    return prev;
};

const reduceCollectFirst = (prev, {objectPath, value}) => {
    if(value) {
        return { _break: value };
    }

    return prev;
};

const reduceCollectFirstError = (prev, {objectPath, value}) => {
    // let error = get(value, 'status') ===  identify.STATUS_ERRORED;
    let error = get(value, 'error');

    // console.log('reduceCollectFirstError', objectPath, error);
    
    if(error) {
        return { _break: { error, objectPath } };
    }
    
    return prev;
};

const reduceNotLoading = (...args) => {
    return reduceStatus({isWrongWhen: isLoading})(...args);
};

const reduceIsPresent = (...args) => {
    return reduceStatus({isWrongWhen: isNotPresent})(...args);
};

const reduceIsLoading = (...args) => {
    return reduceStatus({isTrueWhen: isLoading})(...args);
};

const reduceIsNotInvalidated = (...args) => {
    return reduceStatus({isWrongWhen: isInvalidated})(...args);
};

let modelStates = 'itemData,saveResult,deleteResult,itemCustomActionResult,results';

const resultsReady = ({modelId}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        isFinished: {
            value: true,
            reduce: reduceIsPresent,
            paths: [
                `loadedModels.${modelId}`,
                `ui.loadedModels.${modelId}{results,parent{itemData}}`
            ]
        },

        notLoading: {
            value: true,
            reduce: reduceNotLoading,

            paths: [
                `ui.loadedModels.${modelId}{parent{itemData}}`
            ]
        }
    }
});

const resultsLoading = ({modelId}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: false,
            reduce: reduceIsLoading,

            paths: [
                `ui.loadedModels.${modelId}{results}`
            ]
        }
    }
});

const searchResultsLoading = () => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: false,
            reduce: reduceIsLoading,

            paths: [
                `ui.searchModels`,
            ]
        },
    }
});

const searchResultLoading = ({modelName}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: false,
            reduce: reduceIsLoading,

            paths: [
                `ui.searchModels.${modelName}.results`,
            ]
        },
    }
});


const searchResultsReady = () => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        isFinished: {
            value: true,
            reduce: reduceIsPresent,
            paths: [
                `modelCacheStatus`
            ]
        },
    }
});



const resultsValid = ({modelId}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: true,
            reduce: reduceIsNotInvalidated,

            paths: [
                `ui.loadedModels.${modelId}{resultsInvalid}`
            ]
        }
    }
});

const itemReady = ({modelId}) => ({
    value: true,

    reduce: reduceFalsify,

    /*reduceGroup: (prev, {name, value}) => {
        prev[name] = value;
        return prev;
    },

    process: (value) => {

    },*/

    groups: {
        isFinished: {
            value: true,
            reduce: reduceIsPresent,

            paths: [
                `loadedModels.${modelId}{subviews}`,
                `ui.loadedModels.${modelId}{itemData}`
            ]
        },
       /* notInvalidated: {
            value: true,
            reduce: reduceIsNotInvalidated,

            paths: [
                `ui.loadedModels.${modelId}{subviews{results}}`
            ]
        },*/
    }
});

const itemLoading = ({modelId}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: false,
            reduce: reduceIsLoading,

            paths: [
                `ui.loadedModels.${modelId}{itemData,saveResult,deleteResult,itemCustomActionResult}`
            ]
        },
    }
});

const actionLoading = ({modelId}) => ({
    value: true,
    reduce: reduceFalsify,

    groups: {
        loading: {
            value: false,
            reduce: reduceIsLoading,

            paths: [
                `ui.loadedModels.${modelId}{customActionResult,itemCustomActionResult}`
            ]
        },
    }
});

const modelLoadingError = ({modelId}) => ({
    value: false,
    reduce: reduceCollectFirst,

    groups: {
        loading: {
            value: false,
            reduce: reduceCollectFirstError,

            paths: [
                `ui.loadedModels.${modelId}{${modelStates}}`
            ]
        },
    }
});

const modelDataLoadingError = ({modelId}) => ({
    value: false,
    reduce: reduceCollectFirst,

    groups: {
        loading: {
            value: false,
            reduce: reduceCollectFirstError,

            paths: [
                'models'
            ]
        },
    }
});

const loadingDefinitions = {
    resultsReady,
    resultsLoading,
    itemReady,
    itemLoading,
    resultsValid,
    modelLoadingError,
    modelDataLoadingError,
    searchResultsLoading,
    searchResultsReady,
    searchResultLoading,
    actionLoading
};

export { loadingDefinitions, loadingSchema };

export default function getLoadingState(state, props) {
    let { ready = [], loading = [], error = [], ...definitionProps } = props,
        definitionNames = uniq([...ready, ...loading, ...error]),
        definitions = {};

    for(let definitionName of definitionNames) {
        definitions[definitionName] = loadingDefinitions[definitionName](definitionProps);
    }

    let conclusions = loadingSchema(definitions)(state);

    return {
        error: error.length === 0 ? false : first(values(pick(conclusions, error))),
        ready: ready.length === 0 ? true : min(values(pick(conclusions, ready))),
        loading: loading.length === 0 ? false : max(values(pick(conclusions, loading))),
    }
}

export function getItemLoadingState(state, modelId) {
    return getLoadingState(state, {
        ready: ['itemReady', 'resultsValid'],
        loading: ['itemLoading'],
        error: ['modelLoadingError'],
        modelId
    });
}